import { useMutation } from '@tanstack/react-query';
import axios from 'axios';

export interface CN10ReportParams {
  mailCategories: string[];
  formatType: 'html' | 'pdf' | 'xls';
  fromQuarter?: number;
  toQuarter?: number;
  fromYear?: number;
  toYear?: number;
  inboundOrOutboundFilter?: string;
  designatedOperator?: string;
}

export interface CN10ReportItem {
  officeOfOrigin: string;
  officeOfDestination: string;
  month: number;
  quarter: number;
  normalNetweight: number;
  normalNetweightFormatted: string;
  normalItems: number;
  normalItemsFormatted: string;
  bulkyNetWeight: number;
  bulkyNetWeightFormatted: string;
  bulkyItems: number;
  bulkyItemsFormatted: string;
  totalnetWeight: number;
  totalnetWeightFormatted: string;
  totalItems: number;
  totalItemsFormatted: string;
}

export interface CN10FileData {
  filename: string;
  total: {
    normalNetweight: string;
    normalNetweightFormatted: string;
    normalItems: string;
    normalItemsFormatted: string;
    bulkyNetWeight: string;
    bulkyNetWeightFormatted: string;
    bulkyItems: string;
    bulkyItemsFormatted: string;
    totalnetWeight: string;
    totalnetWeightFormatted: string;
    totalItems: string;
    totalItemsFormatted: string;
  };
  designatedOperator: string;
  reportItems: CN10ReportItem[];
  mailCategory: string;
}

export type CN10DataStructure = CN10FileData[];

export interface CN10ReportResponse {
  // For HTML format, contains the table data
  data?: CN10DataStructure;
  url?: string;
  content?: Blob;
  filename?: string; // Add filename field for PDF/XLS downloads
  noData?: boolean; // Flag to indicate 204 response (no content)
}

export function useCN10Report() {
  return useMutation<CN10ReportResponse, Error, CN10ReportParams>({
    mutationFn: async (params: CN10ReportParams) => {
      // Create URLSearchParams to handle array parameters without brackets
      const searchParams = new URLSearchParams();
      
      // Add mail categories
      params.mailCategories.forEach(category => {
        searchParams.append('mailCategories', category);
      });
      
      // Add format type
      searchParams.append('formatType', params.formatType);
      
      // Add optional parameters
      if (params.fromQuarter) {
        searchParams.append('fromQuarter', params.fromQuarter.toString());
      }
      if (params.toQuarter) {
        searchParams.append('toQuarter', params.toQuarter.toString());
      }
      if (params.fromYear) {
        searchParams.append('fromYear', params.fromYear.toString());
      }
      if (params.toYear) {
        searchParams.append('toYear', params.toYear.toString());
      }
      if (params.inboundOrOutboundFilter) {
        searchParams.append('inboundOrOutboundFilter', params.inboundOrOutboundFilter);
      }
      if (params.designatedOperator) {
        searchParams.append('designatedOperator', params.designatedOperator);
      }

      const response = await axios.get(`/reports/cn10?${searchParams.toString()}`, {
        // HTML returns JSON with table data, PDF/XLS return binary files
        responseType: params.formatType === 'html' ? 'json' : 'blob',
        validateStatus: (status) => {
          // Accept 200 (success) and 204 (no content) as valid responses
          return status === 200 || status === 204;
        }
      });

      // Handle 204 No Content response
      if (response.status === 204) {
        return { noData: true };
      }

      // For HTML format, return the table data
      if (params.formatType === 'html') {
        return { data: response.data };
      }

      // For PDF/XLS, return the blob for file download
      let fileName: string | null = null;
      
      // Try to extract the filename from the Content-Disposition header
      const contentDisposition = 
        response.headers['content-disposition'] || 
        response.headers['Content-Disposition'];
      
      if (contentDisposition) {
        // Match the filename in Content-Disposition header
        const filenameMatch = /filename[^;=\n]*=(['"]?)([^"';]+)\1/i.exec(contentDisposition);
        if (filenameMatch && filenameMatch[2]) {
          fileName = decodeURIComponent(filenameMatch[2]);
        }
      }
      
      // If we couldn't get the filename from the header, generate one
      if (!fileName) {
        // Extract the current date in YYYYMMDD format for filename
        const date = new Date();
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const timestamp = String(date.getHours()).padStart(2, '0') + 
                          String(date.getMinutes()).padStart(2, '0') + 
                          String(date.getSeconds()).padStart(2, '0');
        
        // Format filename with timestamp
        fileName = `CN10_Report_${year}${month}${day}_${timestamp}.${params.formatType}`;
      }
      
      const mimeType = params.formatType === 'pdf' 
        ? 'application/pdf' 
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return { 
        content: response.data,
        filename: fileName
      };
    },
  });
} 